import { useMemo, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PageHeader from '../PageHeader';
import SearchBar from '../SearchBar';
import BulkActions from '../BulkActions';
import supabase from '../../../lib/supabaseClient';
import { API_ENDPOINTS, API_BASE_URL } from '../../../config/api';

const STATUS_OPTIONS = ['All Status', 'Pending', 'Assigned', 'Contacted', 'Completed', 'Cancelled'];
const ADMIN_API_BASE = `${API_BASE_URL}/api/admin`;

const Card = ({ title, value, border, icon, iconBg }) => (
  <div className={`bg-white rounded-xl p-4 md:p-5 border ${border} shadow-[0_6px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_10px_34px_rgba(0,0,0,0.10)] transition-all flex items-center justify-between`}>
    <div>
      <div className="text-sm text-gray-600 mb-1">{title}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
    </div>
    <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center`}>
      {icon}
    </div>
  </div>
);

const StatusCards = ({ counts }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
    <Card
      title="Pending"
      value={counts.Pending || 0}
      border="border-yellow-200"
      iconBg="bg-yellow-50"
      icon={
        <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    />
    <Card
      title="Assigned"
      value={counts.Assigned || 0}
      border="border-purple-200"
      iconBg="bg-purple-50"
      icon={
        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      }
    />
    <Card
      title="Contacted"
      value={counts.Contacted || 0}
      border="border-blue-200"
      iconBg="bg-blue-50"
      icon={
        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      }
    />
    <Card
      title="Completed"
      value={counts.Completed || 0}
      border="border-green-200"
      iconBg="bg-green-50"
      icon={
        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    />
    <Card
      title="Cancelled"
      value={counts.Cancelled || 0}
      border="border-gray-200"
      iconBg="bg-gray-50"
      icon={
        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      }
    />
  </div>
);

const LeadsTable = ({ leads, selected, onToggle, onToggleAll, onEdit, onDistribute }) => {
  const all = leads.length > 0 && selected.length === leads.length;
  const some = selected.length > 0 && selected.length < leads.length;

  return (
    <div className="bg-white rounded-xl shadow-[0_6px_24px_rgba(0,0,0,0.06)] border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={all}
                  ref={el => { if (el) el.indeterminate = some; }}
                  onChange={onToggleAll}
                />
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Lead ID</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Move Details</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Assign</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {leads.map(l => (
              <tr key={l.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-4">
                  <input type="checkbox" className="rounded border-gray-300" checked={selected.includes(l.id)} onChange={() => onToggle(l.id)} />
                </td>
                <td className="px-6 py-4 font-medium text-gray-900">
                  <span className="text-blue-600 font-semibold">Lead #{l.leadNumber || 'N/A'}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{l.customer.name}</div>
                  <div className="text-gray-500">{l.customer.email}</div>
                </td>
                <td className="px-6 py-4">
                  <div>{l.details.move}</div>
                  <div className="text-gray-500 text-xs">{l.details.distance} • {l.details.date}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${l.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                    l.status === 'Distributed' ? 'bg-purple-100 text-purple-800' :
                      l.status === 'Claimed' ? 'bg-green-100 text-green-800' :
                        l.status === 'On Hold' ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'
                    }`}>{l.status}</span>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => onDistribute(l)} className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg" title="Assign Movers">
                    {/* User icon */}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A7 7 0 0112 15a7 7 0 016.879 2.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </button>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{l.created}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Modal = ({ isOpen, onClose, title, children, footer }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-4xl max-h-[90vh] shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">✕</button>
        </div>
        <div className="px-6 py-5 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="px-6 py-4 border-t bg-gray-50 flex-shrink-0">{footer}</div>}
      </div>
    </div>
  );
};

const LeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('All Status');
  const [selected, setSelected] = useState([]);
  const [editLead, setEditLead] = useState(null);
  const [showDistribute, setShowDistribute] = useState(false);
  const [distributeFor, setDistributeFor] = useState(null);
  const [selectedMovers, setSelectedMovers] = useState([]);
  const [availableMovers, setAvailableMovers] = useState([]);
  const [sortedMovers, setSortedMovers] = useState([]);
  const [userSelectedMovers, setUserSelectedMovers] = useState([]); // Movers selected by user when requesting quote
  const [loadingDistances, setLoadingDistances] = useState(false);
  const [distributionTab, setDistributionTab] = useState('recommended'); // 'recommended', 'all'
  const [assigningMovers, setAssigningMovers] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [emailsSentCount, setEmailsSentCount] = useState(0);

  // Fetch leads from database
  useEffect(() => {
    fetchLeads();
    fetchMovers();

    // Set up real-time subscription
    const subscription = supabase
      .channel('admin_leads_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads'
        },
        (payload) => {
          fetchLeads();
        }
      )
      .subscribe();

    // Auto-refresh every 5 minutes
    const intervalId = setInterval(() => {
      fetchLeads();
      fetchMovers();
    }, 5 * 60 * 1000); // 5 minutes

    // Cleanup subscription and interval on unmount
    return () => {
      supabase.removeChannel(subscription);
      clearInterval(intervalId);
    };
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }


      const transformedLeads = (data || []).map(lead => ({
        id: lead.id,
        leadNumber: lead.lead_number,
        customer: {
          name: lead.customer_first_name && lead.customer_last_name
            ? `${lead.customer_first_name} ${lead.customer_last_name}`.trim()
            : 'N/A',
          email: lead.customer_email || 'N/A',
          phone: lead.customer_phone || 'N/A'
        },
        details: {
          move: `${lead.origin_city || 'N/A'} → ${lead.destination_city || 'N/A'}`,
          distance: '', // Calculate if needed
          date: lead.move_date ? new Date(lead.move_date).toLocaleDateString() : 'N/A'
        },
        status: lead.status || 'Pending',
        distribution: lead.assigned_mover_id ? '1 mover assigned' : null,
        created: new Date(lead.created_at).toLocaleDateString(),
        rawData: lead
      }));

      setLeads(transformedLeads);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchMovers = async () => {
    try {
      const { data, error } = await supabase
        .from('movers_data')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      const movers = data || [];
      const emails = movers.map((mover) => mover.email).filter(Boolean);

      let banStatusMap = {};
      if (emails.length > 0) {
        try {
          const response = await fetch(`${ADMIN_API_BASE}/get-mover-ban-status`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ emails })
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.banStatus) {
              banStatusMap = result.banStatus;
            }
          }
        } catch (apiError) {
          // Fall back to local mover flags if admin API is unavailable
        }
      }

      // Show only movers with lead capacity who are not paused/suspended
      const availableMovers = movers.filter(mover => {
        const totalLeads = Number(mover.total_leads || 0);
        const consumedLeads = Number(mover.consumed_leads || 0);
        const isPaused = mover.account_paused === true;
        const isBanned = Boolean(banStatusMap[mover.email]?.banned);
        const isSuspendedByStatus = String(mover.status || '').toLowerCase() === 'suspended';

        // Filter out paused and suspended accounts
        if (isPaused || isBanned || isSuspendedByStatus) {
          return false;
        }

        // Must have leads allocated
        if (totalLeads <= 0) {
          return false;
        }

        // If consumed_leads is less than total_leads, mover has capacity
        const hasCapacity = consumedLeads < totalLeads;
        return hasCapacity;
      });

      setAvailableMovers(availableMovers);
    } catch (error) {
    }
  };

  // Calculate distance between two zip codes in miles
  const calculateZipDistance = async (zip1, zip2) => {
    if (!zip1 || !zip2) return null;

    try {
      // Fetch coordinates for both ZIP codes using zippopotam.us API
      const [coords1, coords2] = await Promise.all([
        fetch(`https://api.zippopotam.us/us/${zip1}`).then(r => r.ok ? r.json() : null),
        fetch(`https://api.zippopotam.us/us/${zip2}`).then(r => r.ok ? r.json() : null)
      ]);

      if (!coords1?.places?.[0] || !coords2?.places?.[0]) return null;

      const lat1 = parseFloat(coords1.places[0].latitude);
      const lon1 = parseFloat(coords1.places[0].longitude);
      const lat2 = parseFloat(coords2.places[0].latitude);
      const lon2 = parseFloat(coords2.places[0].longitude);

      // Haversine formula
      const R = 3959; // Earth's radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;
      return Math.round(distance);
    } catch (error) {
      return null;
    }
  };

  // Sort movers by distance from lead's origin zip code
  const getSortedMoversByDistance = async (leadZip) => {
    if (!leadZip) return availableMovers;

    const moversWithDistance = await Promise.all(
      availableMovers.map(async (mover) => ({
        ...mover,
        distance: await calculateZipDistance(leadZip, mover.zip_code)
      }))
    );

    return moversWithDistance.sort((a, b) => {
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
  };

  const filtered = useMemo(() => {
    return leads.filter(l => {
      const q = query.toLowerCase();
      const matchQ = !q ||
        String(l.leadNumber).toLowerCase().includes(q) ||
        l.customer.name.toLowerCase().includes(q) ||
        l.customer.email.toLowerCase().includes(q);
      const matchS = status === 'All Status' || l.status === status;
      return matchQ && matchS;
    });
  }, [leads, query, status]);

  const counts = useMemo(() => {
    const base = { Pending: 0, 'On Hold': 0, Distributed: 0, Claimed: 0, Quoted: 0 };
    leads.forEach(l => { base[l.status] = (base[l.status] || 0) + 1; });
    return base;
  }, [leads]);

  const onToggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const onToggleAll = () => setSelected(prev => (prev.length === filtered.length ? [] : filtered.map(l => l.id)));

  // Load distances when distribution modal opens
  const handleOpenDistribute = async (lead) => {
    setDistributeFor(lead);
    setShowDistribute(true);
    setLoadingDistances(true);

    // Calculate distances for all movers first
    const sorted = await getSortedMoversByDistance(lead.rawData?.origin_postal_code);
    setSortedMovers(sorted);

    // Get user-selected movers from selected_mover_ids array in the lead
    let userMovers = [];
    if (lead.rawData?.selected_mover_ids && Array.isArray(lead.rawData.selected_mover_ids)) {
      const moverIds = lead.rawData.selected_mover_ids;
      // Get movers from the sorted list (which already has distances calculated)
      userMovers = sorted.filter(m => moverIds.includes(m.id));
    }
    setUserSelectedMovers(userMovers);

    setLoadingDistances(false);
  };

  const confirmDistribute = async () => {
    if (!distributeFor || selectedMovers.length === 0) return;

    setAssigningMovers(true);
    setEmailsSentCount(0);

    try {
      // Update lead with assigned movers (multiple movers can be assigned)
      const { error } = await supabase
        .from('leads')
        .update({
          assigned_mover_ids: selectedMovers,
          status: 'Assigned'
        })
        .eq('id', distributeFor.id);

      if (error) throw error;

      let emailsSent = 0;

      // Send email notifications
      try {
        // 1. Send email to USER about movers being assigned
        if (distributeFor.rawData?.customer_email && distributeFor.rawData?.customer_first_name) {

          const userEmailResponse = await fetch(API_ENDPOINTS.SEND_USER_MOVERS_ASSIGNED, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userEmail: distributeFor.rawData.customer_email,
              userName: `${distributeFor.rawData.customer_first_name} ${distributeFor.rawData.customer_last_name || ''}`.trim(),
              assignedMoversCount: selectedMovers.length
            })
          });

          const userEmailData = await userEmailResponse.json();

          if (userEmailResponse.ok && userEmailData.success) {
            emailsSent++;
          } else {
          }
        } else {
        }

        // 2. Send email to each MOVER about new lead
        const assignedMovers = sortedMovers.filter(m => selectedMovers.includes(m.id));

        for (const mover of assignedMovers) {
          if (mover.email) {

            const moverEmailResponse = await fetch(API_ENDPOINTS.SEND_MOVER_NEW_LEAD, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                moverEmail: mover.email,
                moverName: mover.company_name,
                leadDetails: {
                  moveType: distributeFor.rawData?.move_type,
                  from: `${distributeFor.rawData?.origin_city || ''}, ${distributeFor.rawData?.origin_state || ''} ${distributeFor.rawData?.origin_postal_code || ''}`.trim(),
                  to: `${distributeFor.rawData?.destination_city || ''}, ${distributeFor.rawData?.destination_state || ''} ${distributeFor.rawData?.destination_postal_code || ''}`.trim(),
                  moveDate: distributeFor.rawData?.move_date,
                  propertySize: distributeFor.rawData?.property_size
                }
              })
            });

            const moverEmailData = await moverEmailResponse.json();

            if (moverEmailResponse.ok && moverEmailData.success) {
              emailsSent++;
            } else {
            }
          } else {
          }
        }
      } catch (emailError) {
        // Continue anyway - email failure shouldn't stop the assignment
      }

      setEmailsSentCount(emailsSent);

      // Refresh leads
      await fetchLeads();

      // Show success popup
      setShowSuccessPopup(true);

      // Close distribute modal after a short delay
      setTimeout(() => {
        closeDistribute();
      }, 100);
    } catch (error) {
      toast.error('Failed to assign mover');
    } finally {
      setAssigningMovers(false);
    }
  };

  const onSaveEdit = async () => {
    if (!editLead) return;

    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: editLead.status })
        .eq('id', editLead.id);

      if (error) throw error;

      // Refresh leads
      await fetchLeads();
      setEditLead(null);
    } catch (error) {
      toast.error('Failed to update lead status');
    }
  };

  const closeDistribute = () => {
    setShowDistribute(false);
    setDistributeFor(null);
    setSelectedMovers([]);
    setDistributionTab('recommended');
    setSortedMovers([]);
    setUserSelectedMovers([]);
    setAssigningMovers(false);
  };

  return (
    <div>
      <PageHeader title="Lead Management" subtitle="Control lead workflow and distribution" />

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <p className="text-gray-600 mt-4">Loading leads...</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
            <SearchBar value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by lead #, customer, or email..." />
            <div className="min-w-[160px]">
              <div className="relative">
                <select value={status} onChange={e => setStatus(e.target.value)} className="w-full pl-3 pr-8 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300">
                  {STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
                </select>
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">▾</span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <BulkActions
              selectedCount={selected.length}
              onActivate={() => toast('Hold action')}
              onSuspend={() => toast('Route action')}
              onDelete={() => toast('Distribute action')}
            />
          </div>

          <div className="mb-6">
            <StatusCards counts={counts} />
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl p-12 border text-center">
              <div className="text-4xl mb-4">📋</div>
              <p className="text-gray-600">No leads found</p>
              <p className="text-sm text-gray-500 mt-2">
                {leads.length === 0 ? 'No leads have been created yet' : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <LeadsTable
              leads={filtered}
              selected={selected}
              onToggle={onToggle}
              onToggleAll={onToggleAll}
              onEdit={(l) => setEditLead({ ...l })}
              onDistribute={handleOpenDistribute}
            />
          )}
        </>
      )}

      <Modal
        isOpen={!!editLead}
        onClose={() => setEditLead(null)}
        title={editLead ? `Edit Lead #${editLead.id}` : ''}
        footer={
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditLead(null)} className="px-4 py-2 rounded-lg border">Cancel</button>
            <button onClick={onSaveEdit} className="px-4 py-2 rounded-lg bg-black text-white">Save Changes</button>
          </div>
        }
      >
        {editLead && (
          <div className="grid gap-3">
            <div>
              <label className="text-sm text-gray-600">Status</label>
              <select value={editLead.status} onChange={e => setEditLead({ ...editLead, status: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2">
                {['Pending', 'Assigned', 'Contacted', 'Completed', 'Cancelled'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showDistribute}
        onClose={closeDistribute}
        title={distributeFor ? `Assign Lead #${distributeFor.id}` : 'Assign Lead'}
        footer={
          <div className="flex justify-between items-center">
            <button onClick={closeDistribute} disabled={assigningMovers} className="px-4 py-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
            <button
              onClick={confirmDistribute}
              disabled={selectedMovers.length === 0 || assigningMovers}
              className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${selectedMovers.length === 0 || assigningMovers ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:bg-gray-800'}`}
            >
              {assigningMovers ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Assigning...
                </>
              ) : (
                `Assign to ${selectedMovers.length} Mover${selectedMovers.length === 1 ? '' : 's'}`
              )}
            </button>
          </div>
        }
      >
        {distributeFor && (
          <div className="space-y-6">
            {/* Lead Info */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="border-b border-gray-200 pb-3">
                <div className="text-xs text-gray-500 uppercase mb-1">Customer</div>
                <div className="font-medium text-gray-900">{distributeFor.customer.name}</div>
                <div className="text-sm text-gray-600">{distributeFor.customer.email}</div>
                <div className="text-sm text-gray-600">{distributeFor.customer.phone}</div>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="text-xs text-gray-500 uppercase mb-1">Moving From</div>
                <div className="font-medium text-gray-900">
                  {distributeFor.rawData?.origin_address || distributeFor.rawData?.origin_city}
                </div>
                <div className="text-sm text-gray-600">
                  {distributeFor.rawData?.origin_city}, {distributeFor.rawData?.origin_state} {distributeFor.rawData?.origin_postal_code}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="text-xs text-gray-500 uppercase mb-1">Moving To</div>
                <div className="font-medium text-gray-900">
                  {distributeFor.rawData?.destination_address || distributeFor.rawData?.destination_city}
                </div>
                <div className="text-sm text-gray-600">
                  {distributeFor.rawData?.destination_city}, {distributeFor.rawData?.destination_state} {distributeFor.rawData?.destination_postal_code || ''}
                </div>
              </div>

              {(distributeFor.rawData?.move_date || distributeFor.rawData?.move_type) && (
                <div className="border-t border-gray-200 pt-3 grid grid-cols-2 gap-4">
                  {distributeFor.rawData?.move_date && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Move Date</div>
                      <div className="text-sm text-gray-900">{new Date(distributeFor.rawData.move_date).toLocaleDateString()}</div>
                    </div>
                  )}
                  {distributeFor.rawData?.move_type && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase mb-1">Move Type</div>
                      <div className="text-sm text-gray-900">{distributeFor.rawData.move_type}</div>
                    </div>
                  )}
                </div>
              )}

              {distributeFor.rawData?.assistance_type && (
                <div className="border-t border-gray-200 pt-3">
                  <div className="text-xs text-gray-500 uppercase mb-1">Service Type</div>
                  <div className="text-sm text-gray-900">{distributeFor.rawData.assistance_type}</div>
                </div>
              )}

              {distributeFor.rawData?.project_description && (
                <div className="border-t border-gray-200 pt-3">
                  <div className="text-xs text-gray-500 uppercase mb-1">Additional Details</div>
                  <div className="text-sm text-gray-700">{distributeFor.rawData.project_description}</div>
                </div>
              )}

              {(distributeFor.rawData?.property_size || distributeFor.rawData?.piano_type ||
                distributeFor.rawData?.truck_size || distributeFor.rawData?.vehicle_type) && (
                  <div className="border-t border-gray-200 pt-3">
                    <div className="text-xs text-gray-500 uppercase mb-1">Special Requirements</div>
                    <div className="text-sm text-gray-700 space-y-1">
                      {distributeFor.rawData?.property_size && <div>Property: {distributeFor.rawData.property_size}</div>}
                      {distributeFor.rawData?.piano_type && (
                        <div>Piano: {distributeFor.rawData.piano_type}
                          {distributeFor.rawData?.piano_floors_stairs && ` (${distributeFor.rawData.piano_floors_stairs} floors/stairs)`}
                        </div>
                      )}
                      {distributeFor.rawData?.truck_size && <div>Truck: {distributeFor.rawData.truck_size}</div>}
                      {distributeFor.rawData?.vehicle_type && (
                        <div>Vehicle: {distributeFor.rawData.vehicle_type}
                          {distributeFor.rawData?.vehicle_make && ` - ${distributeFor.rawData.vehicle_make}`}
                          {distributeFor.rawData?.vehicle_model && ` ${distributeFor.rawData.vehicle_model}`}
                          {distributeFor.rawData?.vehicle_year && ` (${distributeFor.rawData.vehicle_year})`}
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* Currently Distributed To */}
            {distributeFor.rawData?.assigned_mover_ids && distributeFor.rawData.assigned_mover_ids.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-800 mb-3">Currently Distributed To</h3>
                <div className="space-y-3">
                  {distributeFor.rawData.assigned_mover_ids.map(moverId => {
                    const mover = availableMovers.find(m => m.id === moverId);
                    if (!mover) return null;

                    const initials = mover.company_name?.split(' ').map(w => w[0]).slice(0, 2).join('') || 'M';

                    return (
                      <div key={moverId} className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex flex-col gap-3">
                          {/* Header with Company Name */}
                          <div className="flex items-center gap-3">
                            {mover.company_logo ? (
                              <img
                                src={mover.company_logo}
                                alt={mover.company_name}
                                className="w-12 h-12 rounded-full object-cover flex-shrink-0 bg-white"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="w-12 h-12 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-base flex-shrink-0"
                              style={{ display: mover.company_logo ? 'none' : 'flex' }}
                            >
                              {initials}
                            </div>
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="font-semibold text-green-900 text-lg">{mover.company_name}</span>
                            </div>
                          </div>

                          {/* Contact Details - Single Column */}
                          <div className="space-y-2 pl-0">
                            {mover.email && (
                              <div className="flex items-start gap-2">
                                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span className="text-sm text-gray-700">{mover.email}</span>
                              </div>
                            )}
                            {mover.phone && (
                              <div className="flex items-start gap-2">
                                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span className="text-sm text-gray-700">{mover.phone}</span>
                              </div>
                            )}
                            {(mover.city || mover.state || mover.zip_code) && (
                              <div className="flex items-start gap-2">
                                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm text-gray-700">{[mover.city, mover.state, mover.zip_code].filter(Boolean).join(', ')}</span>
                              </div>
                            )}
                            {mover.business_address && (
                              <div className="flex items-start gap-2">
                                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span className="text-sm text-gray-700">{mover.business_address}</span>
                              </div>
                            )}
                            {(mover.years_in_business !== null && mover.years_in_business !== undefined) && (
                              <div className="flex items-start gap-2">
                                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm text-gray-700"><strong>Experience:</strong> {mover.years_in_business} {mover.years_in_business === 1 ? 'year' : 'years'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex gap-6">
                <button
                  onClick={() => setDistributionTab('recommended')}
                  className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${distributionTab === 'recommended'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Recommended by User
                </button>
                <button
                  onClick={() => setDistributionTab('all')}
                  className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${distributionTab === 'all'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  All Movers ({availableMovers.length})
                </button>
              </div>
            </div>

            {/* Movers List */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {loadingDistances ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                  <p className="text-gray-600 text-sm mt-3">Calculating distances...</p>
                </div>
              ) : distributionTab === 'recommended' ? (
                <>
                  {userSelectedMovers.length > 0 ? (
                    <p className="text-sm text-gray-600 mb-3">Movers selected by the user when requesting quote</p>
                  ) : (
                    <p className="text-sm text-gray-600 mb-3">No movers were selected by the user</p>
                  )}
                  {userSelectedMovers.map((m) => {
                    const checked = selectedMovers.includes(m.id);
                    const isAlreadyAssigned = distributeFor.rawData?.assigned_mover_ids?.includes(m.id);
                    const initials = m.company_name?.split(' ').map(w => w[0]).slice(0, 2).join('') || 'M';

                    return (
                      <button
                        key={m.id}
                        onClick={() => {
                          if (isAlreadyAssigned) return;
                          setSelectedMovers(prev =>
                            checked ? prev.filter(id => id !== m.id) : [...prev, m.id]
                          );
                        }}
                        disabled={isAlreadyAssigned}
                        className={`w-full text-left p-4 rounded-lg border transition ${isAlreadyAssigned
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
                          : checked
                            ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500 hover:bg-gray-50'
                            : 'border-gray-200 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-3">
                            {m.company_logo ? (
                              <img
                                src={m.company_logo}
                                alt={m.company_name}
                                className="w-12 h-12 rounded-full object-cover flex-shrink-0 bg-white"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="w-12 h-12 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-semibold text-sm flex-shrink-0"
                              style={{ display: m.company_logo ? 'none' : 'flex' }}
                            >
                              {initials}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="font-semibold text-gray-900">{m.company_name}</div>
                                {isAlreadyAssigned && (
                                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    Already Assigned
                                  </span>
                                )}
                              </div>
                              {m.distance !== null && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Distance: {m.distance} miles
                                </div>
                              )}
                            </div>
                            {checked && (
                              <svg className="w-5 h-5 text-orange-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="space-y-1 text-xs text-gray-600 pl-0">
                            {m.email && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span>{m.email}</span>
                              </div>
                            )}
                            {m.phone && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span>{m.phone}</span>
                              </div>
                            )}
                            {(m.city || m.state || m.zip_code) && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{[m.city, m.state, m.zip_code].filter(Boolean).join(', ')}</span>
                              </div>
                            )}
                            {m.business_address && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span>{m.business_address}</span>
                              </div>
                            )}
                            {(m.years_in_business !== null && m.years_in_business !== undefined) && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span><strong>Experience:</strong> {m.years_in_business} {m.years_in_business === 1 ? 'year' : 'years'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">All available movers sorted by distance</p>
                  {sortedMovers.map((m) => {
                    const checked = selectedMovers.includes(m.id);
                    const isAlreadyAssigned = distributeFor.rawData?.assigned_mover_ids?.includes(m.id);
                    const isPaused = m.account_paused === true;
                    const isDisabled = isAlreadyAssigned || isPaused;
                    const initials = m.company_name?.split(' ').map(w => w[0]).slice(0, 2).join('') || 'M';

                    return (
                      <button
                        key={m.id}
                        onClick={() => {
                          if (isDisabled) return;
                          setSelectedMovers(prev =>
                            checked ? prev.filter(id => id !== m.id) : [...prev, m.id]
                          );
                        }}
                        disabled={isDisabled}
                        className={`w-full text-left p-4 rounded-lg border transition ${isDisabled
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
                          : checked
                            ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500 hover:bg-gray-50'
                            : 'border-gray-200 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-3">
                            {m.company_logo ? (
                              <img
                                src={m.company_logo}
                                alt={m.company_name}
                                className="w-12 h-12 rounded-full object-cover flex-shrink-0 bg-white"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="w-12 h-12 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-semibold text-sm flex-shrink-0"
                              style={{ display: m.company_logo ? 'none' : 'flex' }}
                            >
                              {initials}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-semibold text-gray-900">{m.company_name}</div>
                                {isPaused && (
                                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Account Paused
                                  </span>
                                )}
                                {isAlreadyAssigned && (
                                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    Already Assigned
                                  </span>
                                )}
                              </div>
                              {m.distance !== null && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Distance: {m.distance} miles
                                </div>
                              )}
                            </div>
                            {checked && (
                              <svg className="w-5 h-5 text-orange-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="space-y-1 text-xs text-gray-600 pl-0">
                            {m.email && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span>{m.email}</span>
                              </div>
                            )}
                            {m.phone && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span>{m.phone}</span>
                              </div>
                            )}
                            {(m.city || m.state || m.zip_code) && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{[m.city, m.state, m.zip_code].filter(Boolean).join(', ')}</span>
                              </div>
                            )}
                            {m.business_address && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span>{m.business_address}</span>
                              </div>
                            )}
                            {(m.years_in_business !== null && m.years_in_business !== undefined) && (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span><strong>Experience:</strong> {m.years_in_business} {m.years_in_business === 1 ? 'year' : 'years'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Assignment Successful!</h3>
              <div className="space-y-2 text-gray-600 mb-6">
                <p className="text-lg">
                  <span className="font-semibold text-gray-900">{selectedMovers.length}</span> mover{selectedMovers.length !== 1 ? 's' : ''} assigned
                </p>
                <p className="text-lg">
                  <span className="font-semibold text-gray-900">{emailsSentCount}</span> email{emailsSentCount !== 1 ? 's' : ''} sent successfully
                </p>
              </div>
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="w-full bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsManagement;
